import StyleSheet from '../../utils/helpers/styleSheet'
import {Img} from '../../models/editor/img'
import {useAppDispatch, useAppSelector} from '../../stores/configureStore'
import {
  persistPosition,
  selectImage,
  setInert,
  setInteractedImage,
} from '../../stores/editor/editor.reducer'
import {selectToolboxTabPanel} from '../../stores/toolbox/toolbox.reducer'
import ImgCaption from './ImgCaption'
import ImgText from './ImgText'
import ImgUrl from './ImgUrl'
import ImgMasked from './ImgMasked'
import {useCallback, useEffect, useRef} from 'react'

interface ImgProps {
  img: Img
  enableMaskFrame?: boolean
  className?: string | undefined
  noZoom?: boolean
}

const styles: StyleSheet = {
  innerContainer: {
    position: 'relative',
    lineHeight: 0,
  },
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
  },
  imgInner: {
    position: 'relative',
  },
  maskFrame: {
    position: 'absolute',
    width: '200%',
    height: '200%',
    backgroundColor: 'white',
  },
}

export default function RenderImg({
  img,
  enableMaskFrame = false,
  className,
  noZoom = false,
}: ImgProps) {
  const dispatch = useAppDispatch()
  const {zoom, printArea} = useAppSelector(store => store.editor)
  const mouseEventHandlers = {
    onMouseDown: useCallback(
      (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (e.button === 0) {
          e.preventDefault()
          dispatch(persistPosition({id: img.id}))
          dispatch(setInteractedImage({interaction: 'dragging', id: img.id}))
          dispatch(setInert({inert: true, exceptId: img.id}))
          dispatch(selectImage({id: img.id}))
          dispatch(selectToolboxTabPanel({tabPanel: 'Edit'}))
        }
      },
      [dispatch, img.id],
    ),
    onMouseUp: useCallback(
      (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        e.preventDefault()
        dispatch(setInteractedImage(null))
        dispatch(setInert({inert: false}))
      },
      [dispatch],
    ),
  }

  const zoomTransform = useCallback(() => {
    if (noZoom) {
      return ''
    } else {
      return `translate(${zoom.translate.x.toFixed(
        10,
      )}px, ${zoom.translate.y.toFixed(10)}px) scale(${zoom.level.toFixed(10)})`
    }
  }, [zoom, noZoom])

  const transform = useCallback((): string => {
    return `translate(-50%, -50%) ${zoomTransform()} translate(${-(
      printArea?.position?.x ?? 0
    )}px, ${-(printArea?.position?.y ?? 0)}px) translate(${img.position.x}px, ${
      img.position.y
    }px) rotate(${img.rotation}deg) scale(${img.scale}) ${
      img.mirroredX ? 'scaleX(-1)' : ''
    } ${img.mirroredY ? 'scaleY(-1)' : ''}`
  }, [
    printArea?.position,
    img.position,
    img.rotation,
    img.scale,
    img.mirroredX,
    img.mirroredY,
    zoomTransform,
  ])

  const filter = useCallback((): string => {
    if (enableMaskFrame) {
      return 'initial'
    }
    return `${
      img.brightness < 0.95 || img.brightness > 1.05
        ? `brightness(${img.brightness})`
        : ''
    } ${img.filters.inverse ? 'invert(1)' : ''} ${
      img.filters.sepia ? 'sepia(1)' : ''
    } ${img.filters.monochrome ? 'saturate(0)' : ''}`
  }, [img.filters, img.brightness, enableMaskFrame])

  const ref = useRef<HTMLDivElement>(null)

  const onTouchStart = useCallback(
    function (this: HTMLDivElement, e: TouchEvent) {
      if (e.touches.length === 1) {
        e.preventDefault()
        dispatch(persistPosition({id: img.id}))
        dispatch(setInteractedImage({interaction: 'dragging', id: img.id}))
        dispatch(setInert({inert: true, exceptId: img.id}))
        dispatch(selectImage({id: img.id}))
        dispatch(selectToolboxTabPanel({tabPanel: 'Edit'}))
      }
    },
    [dispatch, img.id],
  )
  const onTouchEnd = useCallback(
    function (this: HTMLDivElement, e: TouchEvent) {
      if (e.touches.length === 0) {
        dispatch(setInteractedImage(null))
        dispatch(setInert({inert: false}))
      }
    },
    [dispatch],
  )

  useEffect(() => {
    if (ref.current) {
      const refCurrent = ref.current

      refCurrent.addEventListener('touchstart', onTouchStart, {passive: false})
      refCurrent.addEventListener('touchend', onTouchEnd, {passive: false})
      return () => {
        refCurrent.removeEventListener('touchstart', onTouchStart)
        refCurrent.removeEventListener('touchend', onTouchEnd)
      }
    }
  }, [onTouchStart, onTouchEnd])

  return img.type !== 'caption' ? (
    <div
      className={className}
      style={{
        ...styles.container,
        transform: transform(),
        WebkitTransform: transform(),
        pointerEvents: !img.locked && !img.inert ? 'initial' : 'none',
      }}>
      {enableMaskFrame && (
        <>
          <div
            style={{
              ...styles.maskFrame,
              left: 0,
              bottom: 'calc(100% - 5px)',
            }}></div>
          <div
            style={{
              ...styles.maskFrame,
              top: 0,
              left: 'calc(100% - 5px)',
            }}></div>
          <div
            style={{
              ...styles.maskFrame,
              right: 0,
              top: 'calc(100% - 5px)',
            }}></div>
          <div
            style={{
              ...styles.maskFrame,
              bottom: 0,
              right: 'calc(100% - 5px)',
            }}></div>
        </>
      )}
      <div
        id={img.id}
        style={{
          ...styles.innerContainer,
          filter: filter(),
          cursor: !img.locked && !img.inert ? 'pointer' : 'default',
          pointerEvents: !img.locked && !img.inert ? 'initial' : 'none',
        }}
        ref={ref}
        {...mouseEventHandlers}>
        {(() => {
          switch (img.type) {
            case 'text':
              return <ImgText img={img} />
            case 'url':
              return <ImgUrl img={img} />
            case 'masked':
              return <ImgMasked img={img} />
            default:
              return <></>
          }
        })()}
      </div>
    </div>
  ) : (
    <div
      className={className}
      id={img.id}
      style={{
        ...styles.container,
        transform: transform(),
        WebkitTransform: transform(),
      }}>
      <div style={{...styles.innerContainer, filter: filter()}}>
        <ImgCaption
          img={img}
          onMouseDown={mouseEventHandlers.onMouseDown}
          onMouseUp={mouseEventHandlers.onMouseUp}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        />
      </div>
    </div>
  )
}
